import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import SelectInput from "@commons/Input/Select";
import UploadInput from "@commons/Input/Upload";
import {
  SITUATION_OPTIONS,
  SITUATION_VALUES,
} from "@typings/entities/individuals";
import { config } from "@/config";
import {
  validateCoordinates,
  validateLatitude,
  validateLongitude,
  validatePositiveInteger,
  validatePositiveNumber,
} from "@utils/formatters";
import Text from "@commons/Typography/Text";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { createIndividualAdditionalData } from "@services/individuals/additionalData/create";
import { globalMutate } from "@utils/swr";
import { createAdditionalDataAttachments } from "@services/individuals/additionalData/createAttachments";
import Map from "@commons/Map";
import PublicIcon from "@mui/icons-material/Public";
import { useModalOpenWithCoordinates } from "@hooks/useModalOpenWithCoordinates";

const individualDataSchema = z
  .object({
    situation: z
      .enum(SITUATION_VALUES, {
        error: "Situação deve ser um valor válido",
      })
      .optional(),
    ageEstimateYear: z
      .string()
      .optional()
      .refine((val) => !val || validatePositiveInteger()(val), {
        message: "Idade deve ser um número inteiro positivo",
      }),
    weightKg: z
      .string()
      .optional()
      .refine((val) => !val || validatePositiveNumber()(val), {
        message: "Peso deve ser maior que zero",
      }),
    observations: z.string().optional(),
    attachments: z
      .array(z.instanceof(File))
      .max(
        config.uploadArchive.maxFiles,
        `Você pode enviar no máximo ${config.uploadArchive.maxFiles} arquivos.`,
      )
      .optional(),
    recaptureDate: z.string().optional(),
    latitude: z
      .string()
      .optional()
      .refine((val) => !val || validateLatitude()(val), {
        message: "Latitude deve estar entre -90 e 90",
      }),
    longitude: z
      .string()
      .optional()
      .refine((val) => !val || validateLongitude()(val), {
        message: "Longitude deve estar entre -180 e 180",
      }),
  })
  .superRefine((data, ctx) => {
    if (data.latitude && !data.longitude) {
      ctx.addIssue({
        code: "custom",
        message: "Longitude é obrigatória quando latitude é informada",
        path: ["longitude"],
      });
    }

    if (!data.latitude && data.longitude) {
      ctx.addIssue({
        code: "custom",
        message: "Latitude é obrigatória quando longitude é informada",
        path: ["latitude"],
      });
    }
  });

type AdditionalDataForm = z.infer<typeof individualDataSchema>;

type ModalNewDataIndividualProps = {
  individualId: string;
};

function ModalNewDataIndividual({ individualId }: ModalNewDataIndividualProps) {
  const { isModalOpen, closeModal, setIsLoading, setDataModal } = usePage();
  const open = isModalOpen("newDataIndividual");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<AdditionalDataForm>({
    resolver: zodResolver(individualDataSchema),
    defaultValues: {
      situation: undefined,
      ageEstimateYear: undefined,
      weightKg: undefined,
      recaptureDate: undefined,
      latitude: undefined,
      longitude: undefined,
      observations: undefined,
      attachments: [],
    },
  });

  useModalOpenWithCoordinates<AdditionalDataForm>({ open, setValue });

  const onSubmit = async (data: AdditionalDataForm) => {
    const hasAnyFieldFilled = Boolean(
      data.situation ||
        data.ageEstimateYear ||
        data.weightKg ||
        data.recaptureDate ||
        data.latitude ||
        data.longitude ||
        data.observations ||
        (data.attachments && data.attachments.length > 0),
    );

    if (!hasAnyFieldFilled) {
      toast.warning("Preencha pelo menos um campo antes de salvar.");
      return;
    }

    try {
      setIsLoading(true);

      const createdAdditionalData = await createIndividualAdditionalData({
        id: individualId,
        ...(data.situation && { newIndividualSituation: data.situation }),
        ...(data.recaptureDate && { recaptureDate: data.recaptureDate }),
        ...(data.latitude && { recapturePointLatitude: Number(data.latitude) }),
        ...(data.longitude && {
          recapturePointLongitude: Number(data.longitude),
        }),
        ...(data.ageEstimateYear && { ageYear: Number(data.ageEstimateYear) }),
        ...(data.weightKg && { weightKg: Number(data.weightKg) }),
        ...(data.observations && { observations: data.observations }),
      });

      if (data.attachments && data.attachments.length > 0) {
        await createAdditionalDataAttachments({
          id: individualId,
          additionalDataId: createdAdditionalData.id,
          files: data.attachments,
        });
      }

      toast.success("Dados Adicionais do Indivíduo criados com sucesso");

      await globalMutate([
        `fetch-individual-${individualId}`,
        `fetch-individual-attachments-${individualId}`,
        `fetch-get-individuals-additional-data-${individualId}`,
      ]);
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError
        ? error.message
        : "Erro ao criar dados adicionais do indivíduo";

      toast.error(title);
    } finally {
      setIsLoading(false);
      handleClose();
    }
  };

  const handleClose = () => {
    reset();
    setDataModal({});
    closeModal();
  };

  if (!open) return null;

  const latitude = watch("latitude");
  const longitude = watch("longitude");

  const { hasValidCoordinates, lat, lng } = validateCoordinates(
    latitude,
    longitude,
  );

  return (
    <Modal
      open={open}
      heading="Adicionar Dados ao Indivíduo"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-4xl"
    >
      <form className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <div className="md:col-span-2">
          <Text size="md">Nova Situação do Indivíduo</Text>
          <div className="border border-gray-300 rounded-md p-4 mt-2">
            <Controller
              name="situation"
              control={control}
              render={({ field }) => (
                <SelectInput
                  {...field}
                  value={field.value || ""}
                  label="Situação"
                  size="small"
                  fullWidth
                  error={!!errors.situation}
                  options={SITUATION_OPTIONS}
                />
              )}
            />
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-y-4 xs:gap-x-2 sm:gap-x-3 mt-2">
              <Controller
                name="ageEstimateYear"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Idade (anos)"
                    type="number"
                    textSize="sm"
                    fullWidth
                    error={!!errors.ageEstimateYear}
                    helperText={errors.ageEstimateYear?.message}
                  />
                )}
              />

              <Controller
                name="weightKg"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Peso (kg)"
                    type="number"
                    textSize="sm"
                    fullWidth
                    error={!!errors.weightKg}
                    helperText={errors.weightKg?.message}
                  />
                )}
              />
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <Text size="md">Em caso de recaptura, digite as informações</Text>
          <div className="border border-gray-300 rounded-md p-4 mt-2 grid grid-cols-1 sm:grid-cols-3 gap-y-4 xs:gap-x-2 sm:gap-x-3">
            <div className="flex flex-col col-span-1 gap-4">
              <Controller
                name="recaptureDate"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Data de Recaptura"
                    textSize="sm"
                    fullWidth
                    type="datetime-local"
                    error={!!errors.recaptureDate}
                    helperText={errors.recaptureDate?.message}
                  />
                )}
              />
              <Controller
                name="latitude"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Latitude"
                    type="number"
                    textSize="sm"
                    fullWidth
                    error={!!errors.latitude}
                    helperText={errors.latitude?.message}
                  />
                )}
              />

              <Controller
                name="longitude"
                control={control}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Longitude"
                    type="number"
                    textSize="sm"
                    fullWidth
                    error={!!errors.longitude}
                    helperText={errors.longitude?.message}
                  />
                )}
              />
            </div>
            <div className="h-96 sm:h-full w-full col-span-2">
              {hasValidCoordinates ? (
                <Map.Root>
                  <Map.Localization latitude={lat} longitude={lng} draw />
                </Map.Root>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-slate-100 rounded-md shadow-sm gap-2 p-6 text-center">
                  <PublicIcon className="text-4xl" />
                  <Text size="sm">
                    Insira coordenadas válidas para visualizar o mapa.
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <Controller
            name="observations"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Observações"
                textSize="sm"
                fullWidth
                multiline
                rows={3}
                error={!!errors.observations}
                helperText={errors.observations?.message}
              />
            )}
          />
        </div>

        <div className="md:col-span-2">
          <Controller
            name="attachments"
            control={control}
            render={({ field: { value, onChange, onBlur, name } }) => (
              <UploadInput
                value={value || []}
                onChange={onChange}
                onBlur={onBlur}
                name={name}
                multiple={config.uploadArchive.multiple}
                accept={config.uploadArchive.accept}
                maxFiles={config.uploadArchive.maxFiles}
              />
            )}
          />
          {errors.attachments && (
            <p className="text-red-500 text-sm mt-1">
              {errors.attachments.message}
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default ModalNewDataIndividual;
